import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import type {
  AdminChannel,
  CommunicationAudience,
  CommunicationLocale,
} from "../shared/communications-types";
import {
  activeTranslation,
  localesWithTranslations,
  untranslatedLocales,
} from "../shared/communication-selectors";
import { ChannelBadge } from "../shared/ChannelBadge";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useDeleteChannel, useToggleChannel } from "../catalog/use-communications";
import { AddTranslationDialog } from "../translations/AddTranslationDialog";

interface ChannelCardProps {
  readonly communicationId: string;
  readonly channel: AdminChannel;
  readonly locales: readonly CommunicationLocale[];
  /** From the owning communication — used to mirror the API's email-only
   *  guard so the toggle is disabled instead of failing with a 400. */
  readonly isCritical: boolean;
  readonly audience: CommunicationAudience;
}

export function ChannelCard({
  communicationId,
  channel,
  locales,
  isCritical,
  audience,
}: ChannelCardProps) {
  const { t } = useTranslation();
  const toggle = useToggleChannel();
  const remove = useDeleteChannel();
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const translatedLocales = localesWithTranslations(channel);
  const canAdd = untranslatedLocales(channel, locales).length > 0;
  const busy = toggle.isPending || remove.isPending;

  // Advisory mirror of the engine's email-only invariant (the API is
  // authoritative): a critical / ADMIN communication must keep its EMAIL
  // channel active and can't enable any non-EMAIL channel. Lock the toggle in
  // those cases so the operator never hits CANNOT_DISABLE_EMAIL_FLOOR /
  // CANNOT_ENABLE_NON_EMAIL_ON_CRITICAL. (The "last active channel" guard
  // needs cross-channel state, so that one still surfaces as a server error.)
  const emailOnly = isCritical || audience === "ADMIN";
  const isEmail = channel.channel === "EMAIL";
  const toggleLocked =
    emailOnly && ((isEmail && channel.isActive) || (!isEmail && !channel.isActive));
  // The EMAIL floor of a critical / ADMIN comm can't be removed at all
  // (CANNOT_DISABLE_EMAIL_FLOOR); the "last active channel" guard still
  // surfaces as a server error when it applies.
  const removeLocked = emailOnly && isEmail;

  const handleToggle = async () => {
    if (busy) return;
    setError(null);
    try {
      await toggle.mutateAsync({
        channelId: channel.id,
        input: { isActive: !channel.isActive },
      });
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  const handleRemove = async () => {
    if (busy || removeLocked) return;
    setError(null);
    try {
      await remove.mutateAsync(channel.id);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <section
      data-testid={`communication-channel-card-${channel.channel}`}
      className="space-y-3 rounded-md border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ChannelBadge channel={channel.channel} inactive={!channel.isActive} />
          <span className="text-sm text-muted-foreground">
            {channel.isActive
              ? t("communications.channel.active")
              : t("communications.channel.inactive")}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canAdd && (
            <Button
              size="sm"
              variant="outline"
              data-testid={`communication-add-translation-button-${channel.channel}`}
              onClick={() => setAddOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("communications.translations.add.button")}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            data-testid={`communication-toggle-channel-${channel.channel}`}
            onClick={handleToggle}
            disabled={busy || toggleLocked}
            title={toggleLocked ? t("communications.channel.lockedHint") : undefined}
          >
            {channel.isActive
              ? t("communications.channel.disable")
              : t("communications.channel.enable")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={`communication-delete-channel-${channel.channel}`}
            onClick={handleRemove}
            disabled={busy || removeLocked}
            title={removeLocked ? t("communications.channel.lockedHint") : undefined}
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("communications.channel.remove")}
          </Button>
        </div>
      </div>

      {(toggleLocked || removeLocked) && (
        <p
          data-testid={`communication-channel-locked-${channel.channel}`}
          className="text-xs text-muted-foreground"
        >
          {t("communications.channel.lockedHint")}
        </p>
      )}

      {error && (
        <p
          data-testid={`communication-channel-error-${channel.channel}`}
          className="text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {translatedLocales.length === 0 ? (
        <p
          data-testid={`communication-channel-no-translations-${channel.channel}`}
          className="text-sm text-muted-foreground"
        >
          {t("communications.channel.noTranslations")}
        </p>
      ) : (
        <ul className="space-y-2">
          {translatedLocales.map((localeCode) => {
            const active = activeTranslation(channel, localeCode);
            return (
              <li
                key={localeCode}
                data-testid={`communication-locale-row-${channel.channel}-${localeCode}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background p-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      {localeCode}
                    </code>
                    {active && (
                      <span className="text-xs text-muted-foreground">
                        {t("communications.translations.versionLabel", {
                          version: active.version,
                        })}
                      </span>
                    )}
                  </div>
                  {active?.subject && (
                    <p className="truncate text-sm font-medium">{active.subject}</p>
                  )}
                </div>
                <Link
                  to={`/communications/${communicationId}/channels/${channel.id}/translations/${encodeURIComponent(localeCode)}`}
                  data-testid={`communication-edit-translation-link-${channel.channel}-${localeCode}`}
                  className="shrink-0 text-sm font-medium text-wander-orange hover:underline"
                >
                  {t("communications.actions.edit")}
                  <span aria-hidden="true"> →</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <AddTranslationDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        channel={channel}
        locales={locales}
      />
    </section>
  );
}
